import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { fetcher } from '../../../services/fetcher';
import { useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { createAccDocument, updateAccDocument } from '../../../services/mutations';
import {
  Badge,
  Button,
  Card,
  DateInput,
  InfoIcon,
  ListTreeIcon,
  NumberInput,
  PlusIcon,
  Select,
  TextInput,
  TrashIcon,
} from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { acctFieldLabel } from '../shared/typography';
import { isApOpenItemAccount, effectiveTypeCode } from '../shared/accountTypeUtils';
import { applyDocumentLinePatch, isBalanced, localToUsdAmount, perCompanyBalances, sumDebitCredit, sumDebitCreditUsd, toNumber } from '../shared/balanceUtils';
import { mapLinesToPayload, decodeOpenBalanceNote } from '../shared/documentLinePayload';
import { DocumentLineForm, newDocumentLine } from '../shared/documentLineTypes';
import { resolveRateForCurrency } from '../shared/linkPickerLineUtils';
import { dueDateFromPaymentTerms } from '../shared/paymentTerms';
import { JournalBalanceBar } from '../shared/JournalBalanceBar';
import { formatDate } from '../../../config/appConfig';
import { useAccDocument } from '../documents/useData';
import { PURCHASE_INVOICE_TYPE_CODES } from '../documents/formTemplates';
import { moduleConfig as journalsConfig } from '../documents/config';

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string; hint?: string }> = ({
  label,
  children,
  className,
  hint,
}) => {
  const { colors } = useTheme();
  return (
    <div className={cn('min-w-0', className)}>
      <p className={cn(acctFieldLabel, 'mb-1', colors.textMuted)}>{label}</p>
      {children}
      {hint && <p className={cn('text-[11px] mt-1', colors.textMuted)}>{hint}</p>}
    </div>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode; accent?: 'sky' | 'slate'; right?: React.ReactNode }> = ({
  children,
  accent = 'sky',
  right,
}) => {
  const { colors } = useTheme();
  return (
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            'h-2 w-2 rounded-full shrink-0',
            accent === 'sky' && 'bg-sky-500',
            accent === 'slate' && 'bg-slate-400',
          )}
        />
        <h2 className={cn('text-sm font-semibold', colors.text)}>{children}</h2>
      </div>
      {right}
    </div>
  );
};

type Lookup = {
  id: number;
  code: string;
  name: string;
  combLevel?: string;
  allowOpenItems?: boolean;
  paymentTermsDays?: number | null;
  currencyIds?: number[];
  companyIds?: number[];
  operationalType?: { id: number; code: string; name: string } | null;
  effectiveOperationalType?: { id: number; code: string; name: string } | null;
  accountType?: { id: number; code: string; name: string } | null;
  effectiveAccountType?: { id: number; code: string; name: string } | null;
};

type DocumentTypeLookup = { id: number; code: string; name: string; isActive?: boolean };

type ExpenseSplit = {
  key: string;
  accountId: string;
  amount: string;
  narrative: string;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const round2 = (n: number) => Math.round(n * 100) / 100;
const accountLabel = (a: Lookup) => a.name || a.combLevel || a.code;

const newExpenseSplit = (): ExpenseSplit => ({
  key: `exp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  accountId: '',
  amount: '',
  narrative: '',
});

/** Expense / P&L style accounts — exclude operational bank/cash/AP/AR. */
const isExpenseLikeAccount = (account: Lookup) => {
  const op = effectiveTypeCode(account);
  if (op === 'BANK' || op === 'CASH' || op === 'AP' || op === 'AR') return false;
  return true;
};

export const InvoiceForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useNavigateBack('/accounting/journals', getLocationReturnPath(location.state));
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const editId = id && id !== 'new' ? Number(id) : 0;
  const { item, isLoading } = useAccDocument(editId);
  const rateCacheRef = useRef(new Map<string, { rate: number | null; rateDate: string | null }>());
  const dueDateTouchedRef = useRef(false);

  const [documentTypeId, setDocumentTypeId] = useState('');
  const [documentDate, setDocumentDate] = useState(todayIso);
  const [postingDate, setPostingDate] = useState(todayIso);
  const [description, setDescription] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [vendorAccountId, setVendorAccountId] = useState('');
  const [expenseSplits, setExpenseSplits] = useState<ExpenseSplit[]>(() => [newExpenseSplit()]);
  const [currencyId, setCurrencyId] = useState('');
  const [rate, setRate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dn, setDn] = useState('');
  const [linkNumber, setLinkNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const { data: companiesRaw } = useSWR<Lookup[] | { data: Lookup[] }>('/acc-companies', fetcher);
  const { data: accountsRaw } = useSWR<Lookup[] | { data: Lookup[] }>('/acc-accounts?postingOnly=true', fetcher);
  const { data: currencies } = useSWR<{ id: number; code: string; name: string }[]>('/currencies', fetcher);
  const { data: documentTypesRaw } = useSWR<DocumentTypeLookup[] | { data: DocumentTypeLookup[] }>(
    '/acc-document-types',
    fetcher,
  );

  const companies = Array.isArray(companiesRaw) ? companiesRaw : companiesRaw?.data ?? [];
  const accounts = Array.isArray(accountsRaw) ? accountsRaw : accountsRaw?.data ?? [];
  const documentTypes = Array.isArray(documentTypesRaw) ? documentTypesRaw : documentTypesRaw?.data ?? [];

  const companyOptions = companies.map((c) => ({ value: String(c.id), label: c.name }));

  const vendorAccounts = useMemo(
    () => accounts
      .filter(isApOpenItemAccount)
      .sort((a, b) => (a.combLevel ?? a.code).localeCompare(b.combLevel ?? b.code)),
    [accounts],
  );

  const expenseAccounts = useMemo(
    () => accounts
      .filter(isExpenseLikeAccount)
      .sort((a, b) => (a.combLevel ?? a.code).localeCompare(b.combLevel ?? b.code)),
    [accounts],
  );

  const expenseAccountOptions = useMemo(
    () => [
      { value: '', label: 'Select expense…' },
      ...expenseAccounts.map((a) => ({ value: String(a.id), label: accountLabel(a) })),
    ],
    [expenseAccounts],
  );

  const invoiceTypeOptions = useMemo(() => {
    const rows = documentTypes
      .filter((t) => t.isActive !== false && PURCHASE_INVOICE_TYPE_CODES.includes(t.code as typeof PURCHASE_INVOICE_TYPE_CODES[number]))
      .sort((a, b) => a.code.localeCompare(b.code));
    return rows.map((t) => ({ value: String(t.id), label: t.name }));
  }, [documentTypes]);

  const vendorMeta = vendorAccounts.find((a) => String(a.id) === vendorAccountId);
  const currenciesForVendor = useMemo(() => {
    const list = (currencies ?? []).map((c) => ({ value: String(c.id), label: c.name || c.code }));
    const allowed = vendorMeta?.currencyIds ?? [];
    if (!allowed.length) return list;
    return list.filter((c) => allowed.includes(Number(c.value)));
  }, [currencies, vendorMeta]);

  const currencyCode = currencies?.find((c) => String(c.id) === currencyId)?.code ?? '';

  const filledExpenses = useMemo(
    () => expenseSplits.filter((row) => row.accountId && toNumber(row.amount) > 0.005),
    [expenseSplits],
  );

  const amountNum = useMemo(
    () => round2(filledExpenses.reduce((sum, row) => sum + toNumber(row.amount), 0)),
    [filledExpenses],
  );
  const amountUsdNum = rate && amountNum > 0 ? localToUsdAmount(amountNum, Number(rate)) : 0;
  const amountUsd = amountUsdNum > 0 ? amountUsdNum.toFixed(2) : '';

  const previewLines = useMemo((): DocumentLineForm[] => {
    if (!companyId || !vendorAccountId || filledExpenses.length === 0) return [];
    const expenseLines = filledExpenses.map((row) => {
      const amt = toNumber(row.amount);
      const usd = rate && amt > 0 ? localToUsdAmount(amt, Number(rate)) : 0;
      return applyDocumentLinePatch(newDocumentLine(), {
        companyId,
        accountId: row.accountId,
        currencyId,
        rate,
        debitLocal: amt.toFixed(2),
        debitUSD: usd > 0 ? usd.toFixed(2) : '',
        creditLocal: '',
        creditUSD: '',
        narrative: row.narrative || description || undefined,
        dn: dn || undefined,
        linkNumber: linkNumber || undefined,
      });
    });
    const ap = applyDocumentLinePatch(newDocumentLine(), {
      companyId,
      accountId: vendorAccountId,
      currencyId,
      rate,
      debitLocal: '',
      debitUSD: '',
      creditLocal: amountNum.toFixed(2),
      creditUSD: amountUsd || '',
      narrative: description || undefined,
      dn: dn || undefined,
      linkNumber: linkNumber || undefined,
      dueDate: dueDate || undefined,
      allowOpenItems: true,
    });
    return [...expenseLines, ap];
  }, [
    amountNum,
    amountUsd,
    companyId,
    currencyId,
    description,
    dn,
    dueDate,
    filledExpenses,
    linkNumber,
    rate,
    vendorAccountId,
  ]);

  const balanced = previewLines.length >= 2 && isBalanced(
    previewLines.map((l) => ({
      companyId: Number(l.companyId),
      debitLocal: l.debitLocal,
      creditLocal: l.creditLocal,
    })),
  );

  const balanceLines = previewLines
    .filter((l) => l.companyId)
    .map((l) => ({ companyId: Number(l.companyId), debitLocal: l.debitLocal, creditLocal: l.creditLocal }));
  const usdBalanceLines = previewLines
    .filter((l) => l.companyId)
    .map((l) => ({ debitUSD: l.debitUSD, creditUSD: l.creditUSD }));
  const totals = sumDebitCredit(balanceLines);
  const usdTotals = sumDebitCreditUsd(usdBalanceLines);
  const companyTotals = perCompanyBalances(balanceLines);
  const companyNames = useMemo(() => {
    const m: Record<number, string> = {};
    for (const c of companies) m[c.id] = `${c.name}`;
    return m;
  }, [companies]);

  const canSave = Boolean(
    documentTypeId
    && companyId
    && vendorAccountId
    && filledExpenses.length > 0
    && amountNum > 0.005
    && balanced,
  );

  const patchExpense = (key: string, patch: Partial<ExpenseSplit>) => {
    setExpenseSplits((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const addExpense = () => setExpenseSplits((rows) => [...rows, newExpenseSplit()]);

  const removeExpense = (key: string) => {
    setExpenseSplits((rows) => {
      if (rows.length <= 1) return [{ ...newExpenseSplit(), key: rows[0]?.key ?? newExpenseSplit().key }];
      return rows.filter((row) => row.key !== key);
    });
  };

  useEffect(() => {
    if (documentTypeId || invoiceTypeOptions.length === 0) return;
    const pi = documentTypes.find((t) => t.code === 'PI');
    setDocumentTypeId(String((pi ?? documentTypes.find((t) => PURCHASE_INVOICE_TYPE_CODES.includes(t.code as typeof PURCHASE_INVOICE_TYPE_CODES[number])))?.id ?? invoiceTypeOptions[0].value));
  }, [documentTypeId, documentTypes, invoiceTypeOptions]);

  useEffect(() => {
    if (!editId) {
      setHydrated(true);
      return;
    }
    if (!item || hydrated) return;
    if (item.documentType?.code && !PURCHASE_INVOICE_TYPE_CODES.includes(item.documentType.code as typeof PURCHASE_INVOICE_TYPE_CODES[number])) {
      navigate(`/accounting/journals/${item.id}/edit`, { replace: true });
      return;
    }
    setDocumentTypeId(String(item.documentTypeId));
    setDocumentDate(item.documentDate?.slice(0, 10) ?? todayIso());
    setPostingDate(item.postingDate?.slice(0, 10) ?? todayIso());
    setDescription(item.description ?? '');

    const lines = (item.lines ?? []).map((line) => {
      const { notes } = decodeOpenBalanceNote(line.notes);
      return {
        companyId: String(line.companyId),
        accountId: String(line.accountId),
        debitLocal: line.debitLocal != null ? String(line.debitLocal) : '',
        creditLocal: line.creditLocal != null ? String(line.creditLocal) : '',
        currencyId: line.currencyId != null ? String(line.currencyId) : '',
        rate: line.rate != null ? String(line.rate) : '',
        narrative: line.narrative ?? '',
        dn: line.dn ?? '',
        linkNumber: line.linkNumber ?? '',
        dueDate: line.dueDate?.slice(0, 10) ?? '',
        notes,
      };
    });

    const debitLines = lines.filter((l) => toNumber(l.debitLocal) > 0.005);
    const creditLine = lines.find((l) => toNumber(l.creditLocal) > 0.005);

    if (debitLines.length) {
      setCompanyId(debitLines[0].companyId);
      setCurrencyId(debitLines[0].currencyId);
      setRate(debitLines[0].rate);
      setDn(debitLines[0].dn);
      setLinkNumber(debitLines[0].linkNumber);
      if (!item.description && debitLines[0].narrative) setDescription(debitLines[0].narrative);
      setExpenseSplits(
        debitLines.map((line) => ({
          key: `exp-${line.accountId}-${Math.random().toString(36).slice(2, 8)}`,
          accountId: line.accountId,
          amount: Number(line.debitLocal).toFixed(2),
          narrative: line.narrative && line.narrative !== item.description ? line.narrative : '',
        })),
      );
    }

    if (creditLine) {
      setVendorAccountId(creditLine.accountId);
      setCompanyId((prev) => prev || creditLine.companyId);
      setCurrencyId((prev) => prev || creditLine.currencyId);
      setRate((prev) => prev || creditLine.rate);
      if (creditLine.dueDate) {
        dueDateTouchedRef.current = true;
        setDueDate(creditLine.dueDate);
      }
      if (creditLine.dn) setDn((prev) => prev || creditLine.dn);
      if (creditLine.linkNumber) setLinkNumber((prev) => prev || creditLine.linkNumber);
    }
    setHydrated(true);
  }, [editId, item, hydrated, navigate]);

  useEffect(() => {
    if (dueDateTouchedRef.current) return;
    if (!vendorAccountId || !postingDate) return;
    const terms = vendorMeta?.paymentTermsDays;
    const next = dueDateFromPaymentTerms(postingDate, terms ?? null);
    if (next) setDueDate(next);
  }, [vendorAccountId, postingDate, vendorMeta?.paymentTermsDays]);

  const handleCurrencyChange = async (nextCurrencyId: string) => {
    setCurrencyId(nextCurrencyId);
    if (!nextCurrencyId) {
      setRate('');
      return;
    }
    const resolved = await resolveRateForCurrency(nextCurrencyId, postingDate, rateCacheRef.current);
    setRate(resolved);
  };

  useEffect(() => {
    if (!currencyId || !postingDate) return;
    void (async () => {
      const resolved = await resolveRateForCurrency(currencyId, postingDate, rateCacheRef.current);
      if (resolved) setRate(resolved);
    })();
  }, [postingDate, currencyId]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) {
      toast.error('Fill company, vendor, and at least one expense with amount');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        documentTypeId: Number(documentTypeId),
        documentDate: documentDate || undefined,
        postingDate: postingDate || undefined,
        description: description || undefined,
        lines: mapLinesToPayload(previewLines),
      };
      let docId = editId;
      if (editId) {
        await updateAccDocument(editId, payload);
      } else {
        const created = await createAccDocument(payload) as { id: number };
        docId = created.id;
      }
      toast.success('Invoice saved');
      navigate(docId ? `/accounting/invoices/${docId}/edit` : '/accounting/journals');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (editId && isLoading) {
    return (
      <div className={cn('flex items-center justify-center flex-1 min-h-[240px] text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  const vendorLabel = vendorAccounts.find((a) => String(a.id) === vendorAccountId);
  const selectedType = invoiceTypeOptions.find((t) => t.value === documentTypeId);
  const selectedCompany = companies.find((c) => String(c.id) === companyId);
  const expenseShare = (amt: number) => (amountNum > 0.005 ? round2((amt / amountNum) * 100) : 0);
  const previewDebitLocal = amountNum;
  const previewCreditLocal = amountNum;
  const previewDebitUsd = amountUsdNum;
  const previewCreditUsd = amountUsdNum;

  return (
    <div className="flex flex-col flex-1 h-full min-h-0 -mt-1">
      <form
        onSubmit={onSubmit}
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className={cn('flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b', colors.border, colors.headerBg)}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Badge variant="info" className="font-normal text-[10px] uppercase tracking-wide">Draft</Badge>
              {selectedType && (
                <span className={cn('text-xs truncate', colors.textMuted)}>
                  {selectedType.label}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={cn(
                'hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                canSave ? colors.success : colors.warning,
              )}
            >
              <span className={cn('h-1.5 w-1.5 rounded-full', canSave ? 'bg-emerald-500' : 'bg-amber-500')} />
              {canSave ? 'Ready to save' : 'Complete entry'}
            </span>
            <Button type="button" variant="outline" size="sm" onClick={goBack}>Cancel</Button>
            {editId > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                title="Open full journal lines grid"
                onClick={() => navigate(`${journalsConfig.module.route.edit(editId)}?view=journal`)}
              >
                <ListTreeIcon className="h-3.5 w-3.5" />
                Journal format
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={!canSave || saving}
              loading={saving}
              className="min-w-[5.5rem]"
            >
              Save draft
            </Button>
          </div>
        </div>

        <div className={cn('flex-1 overflow-y-auto dashboard-scrollbar min-h-0', colors.background)}>
          <div className="p-4 grid grid-cols-1 2xl:grid-cols-12 gap-4 2xl:items-start">
            <div className="2xl:col-span-8 space-y-3">
              <Card className={cn('overflow-hidden shadow-sm', colors.border, colors.surface)}>
                <div className="h-1 bg-gradient-to-r from-sky-500 to-cyan-500" />
                <div className="p-4 space-y-3">
                  <div>
                    <SectionTitle>Document</SectionTitle>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <Field label="Type">
                        <Select
                          value={documentTypeId}
                          onChange={(e) => setDocumentTypeId(e.target.value)}
                          options={invoiceTypeOptions}
                          showClearButton={false}
                        />
                      </Field>
                      <Field label="Company">
                        <Select
                          value={companyId}
                          onChange={(e) => setCompanyId(e.target.value)}
                          options={[{ value: '', label: 'Select company…' }, ...companyOptions]}
                        />
                      </Field>
                      <Field label="Doc. date">
                        <DateInput value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} showFormatHint={false} />
                      </Field>
                      <Field label="Posting date">
                        <DateInput value={postingDate} onChange={(e) => setPostingDate(e.target.value)} showFormatHint={false} />
                      </Field>
                    </div>
                  </div>

                  <div className={cn('border-t pt-3', colors.border)}>
                    <SectionTitle>
                      <span className="inline-flex items-center gap-1.5">
                        <span>Vendor</span>
                        <span
                          className="inline-flex"
                          title="AP credit auto-balances the expense total"
                        >
                          <InfoIcon className={cn('h-3.5 w-3.5 shrink-0', colors.primaryFg)} />
                        </span>
                      </span>
                    </SectionTitle>
                    <div className="flex flex-col sm:flex-row flex-wrap lg:flex-nowrap gap-3 items-start">
                      <Field label="Vendor (AP)" className="w-full sm:flex-[1_1_14rem] min-w-0">
                        <Select
                          value={vendorAccountId}
                          onChange={(e) => setVendorAccountId(e.target.value)}
                          options={[
                            { value: '', label: 'Select vendor…' },
                            ...vendorAccounts.map((a) => ({ value: String(a.id), label: accountLabel(a) })),
                          ]}
                        />
                      </Field>
                      <Field label="Currency" className="w-[6.5rem] shrink-0">
                        <Select
                          value={currencyId}
                          onChange={(e) => { void handleCurrencyChange(e.target.value); }}
                          options={[{ value: '', label: '—' }, ...currenciesForVendor]}
                        />
                      </Field>
                      <Field label="Rate" className="w-[6.5rem] shrink-0">
                        <NumberInput
                          value={rate}
                          onChange={(e) => setRate(String(e.target.value ?? ''))}
                          className="text-right tabular-nums h-10 min-h-10"
                          allowDecimals
                        />
                      </Field>
                      <Field label="Due date" className="w-[10.5rem] shrink-0">
                        <DateInput
                          value={dueDate}
                          onChange={(e) => {
                            dueDateTouchedRef.current = true;
                            setDueDate(e.target.value);
                          }}
                          showFormatHint={false}
                        />
                      </Field>
                    </div>
                  </div>

                  <div className={cn('border-t pt-3', colors.border)}>
                    <SectionTitle
                      right={(
                        <Button type="button" variant="outline" size="sm" className="gap-1.5 h-8" onClick={addExpense}>
                          <PlusIcon className="h-3.5 w-3.5" />
                          Add expense
                        </Button>
                      )}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span>Expenses</span>
                        <span
                          className="inline-flex"
                          title="Split the invoice across one or more expense accounts. AP credit = sum of expenses."
                        >
                          <InfoIcon className={cn('h-3.5 w-3.5 shrink-0', colors.primaryFg)} />
                        </span>
                      </span>
                    </SectionTitle>
                    <div className="space-y-2">
                      {expenseSplits.map((row, index) => {
                        const acct = expenseAccounts.find((a) => String(a.id) === row.accountId);
                        return (
                          <div
                            key={row.key}
                            className={cn('rounded-xl border p-3', colors.border, colors.headerBg)}
                          >
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                                Expense {index + 1}
                              </p>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  'h-7 w-7',
                                  isDark ? 'text-red-400' : 'text-red-600',
                                  colors.interactiveHover,
                                )}
                                disabled={expenseSplits.length <= 1}
                                onClick={() => removeExpense(row.key)}
                                aria-label={`Remove expense ${index + 1}`}
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                              <Field label="Account" className="lg:col-span-5">
                                <Select
                                  value={row.accountId}
                                  onChange={(e) => patchExpense(row.key, { accountId: e.target.value })}
                                  options={expenseAccountOptions}
                                />
                              </Field>
                              <Field label={`Amount${currencyCode ? ` (${currencyCode})` : ''}`} className="lg:col-span-3">
                                <NumberInput
                                  value={row.amount}
                                  onChange={(e) => patchExpense(row.key, { amount: String(e.target.value ?? '') })}
                                  className="text-right tabular-nums font-semibold h-10 min-h-10"
                                  allowDecimals
                                />
                              </Field>
                              <Field label="Line note" className="lg:col-span-4">
                                <TextInput
                                  value={row.narrative}
                                  onChange={(e) => patchExpense(row.key, { narrative: e.target.value })}
                                  placeholder={acct ? 'Optional' : 'Optional note'}
                                />
                              </Field>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className={cn('mt-3 flex items-center justify-between gap-3 rounded-lg border px-3 py-2', colors.info, colors.border)}>
                      <span className="text-xs font-medium">
                        Invoice total ({filledExpenses.length} expense{filledExpenses.length === 1 ? '' : 's'})
                      </span>
                      <div className="text-right">
                        <p className={cn('text-sm font-semibold tabular-nums', colors.text)}>
                          {currencyCode || 'Local'} {amountNum > 0.005 ? amountNum.toFixed(2) : '—'}
                        </p>
                        {amountUsd && (
                          <p className={cn('text-[11px] tabular-nums', colors.textMuted)}>USD {amountUsd}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={cn('border-t pt-3', colors.border)}>
                    <SectionTitle accent="slate">Reference</SectionTitle>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <Field label="Description" className="sm:col-span-2 lg:col-span-2">
                        <TextInput
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Invoice narrative…"
                        />
                      </Field>
                      <Field label="DN / reference">
                        <TextInput value={dn} onChange={(e) => setDn(e.target.value)} placeholder="Optional" />
                      </Field>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            <div className="2xl:col-span-4 2xl:sticky 2xl:top-4 space-y-4">
              <Card className={cn('overflow-hidden shadow-sm', colors.border, colors.surface)}>
                <div className={cn('px-4 py-3 border-b', colors.border, colors.headerBg)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <SectionTitle>Posting preview</SectionTitle>
                      <p className={cn('text-xs pl-4 -mt-1', colors.textMuted)}>
                        {previewLines.length === 0
                          ? 'Live journal that will be saved'
                          : `${filledExpenses.length} expense debit${filledExpenses.length === 1 ? '' : 's'} + 1 AP credit · ${previewLines.length} lines`}
                      </p>
                    </div>
                    {previewLines.length > 0 && (
                      <Badge variant={balanced ? 'success' : 'warning'} className="rounded-md font-normal shrink-0">
                        {balanced ? 'Balanced' : 'Out of balance'}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="p-3 space-y-3">
                  {previewLines.length === 0 ? (
                    <div className={cn('rounded-xl border border-dashed px-4 py-10 text-center', colors.border)}>
                      <p className={cn('text-sm font-medium', colors.textSecondary)}>Nothing to post yet</p>
                      <p className={cn('text-xs mt-1 max-w-[16rem] mx-auto', colors.textMuted)}>
                        Add vendor and one or more expenses — AP balances automatically.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Document context */}
                      <div className={cn('rounded-lg border px-3 py-2.5 space-y-2', colors.border, colors.headerBg)}>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
                          <div className="min-w-0">
                            <p className={cn('uppercase tracking-wide font-medium', colors.textMuted)}>Company</p>
                            <p className={cn('font-medium truncate', colors.textSecondary)} title={selectedCompany ? selectedCompany.name : undefined}>
                              {selectedCompany ? selectedCompany.name : '—'}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <p className={cn('uppercase tracking-wide font-medium', colors.textMuted)}>Type</p>
                            <p className={cn('font-medium truncate', colors.textSecondary)} title={selectedType?.label}>
                              {selectedType?.label ?? '—'}
                            </p>
                          </div>
                          <div>
                            <p className={cn('uppercase tracking-wide font-medium', colors.textMuted)}>Doc date</p>
                            <p className={cn('tabular-nums', colors.textSecondary)}>{formatDate(documentDate) || documentDate || '—'}</p>
                          </div>
                          <div>
                            <p className={cn('uppercase tracking-wide font-medium', colors.textMuted)}>Posting</p>
                            <p className={cn('tabular-nums', colors.textSecondary)}>{formatDate(postingDate) || postingDate || '—'}</p>
                          </div>
                        </div>
                        {(description || dn || dueDate || currencyCode || rate) && (
                          <div className={cn('flex flex-wrap gap-1.5 pt-1 border-t', colors.border)}>
                            {currencyCode && (
                              <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px]', colors.surface, colors.border, colors.textSecondary)}>
                                <span className={colors.textMuted}>CCY</span>
                                <span className="font-semibold tabular-nums">{currencyCode}</span>
                                {rate && <span className={cn('tabular-nums', colors.textMuted)}>@ {rate}</span>}
                              </span>
                            )}
                            {dueDate && (
                              <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px]', colors.surface, colors.border, colors.textSecondary)}>
                                <span className={colors.textMuted}>Due</span>
                                <span className="font-medium tabular-nums">{formatDate(dueDate) || dueDate}</span>
                              </span>
                            )}
                            {dn && (
                              <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] max-w-full', colors.surface, colors.border, colors.textSecondary)}>
                                <span className={cn('shrink-0', colors.textMuted)}>DN</span>
                                <span className="font-medium truncate">{dn}</span>
                              </span>
                            )}
                            {description && (
                              <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] max-w-full', colors.surface, colors.border, colors.textSecondary)}>
                                <span className={cn('shrink-0', colors.textMuted)}>Desc</span>
                                <span className="truncate">{description}</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Journal lines */}
                      <div className={cn('rounded-lg border overflow-hidden', colors.border)}>
                        <div className={cn(
                          'grid grid-cols-[1.75rem_minmax(0,1fr)_4.25rem_4.25rem] gap-x-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide border-b',
                          colors.headerBg,
                          colors.textMuted,
                          colors.border,
                        )}>
                          <span>#</span>
                          <span>Account / detail</span>
                          <span className="text-right">Debit</span>
                          <span className="text-right">Credit</span>
                        </div>
                        <div className={cn('divide-y', colors.divide)}>
                          {filledExpenses.map((row, idx) => {
                            const acct = expenseAccounts.find((a) => String(a.id) === row.accountId);
                            const amt = toNumber(row.amount);
                            const usd = rate && amt > 0 ? localToUsdAmount(amt, Number(rate)) : 0;
                            const name = acct?.name ?? acct?.combLevel ?? acct?.code ?? 'Expense';
                            const share = expenseShare(amt);
                            return (
                              <div
                                key={row.key}
                                className={cn(
                                  'grid grid-cols-[1.75rem_minmax(0,1fr)_4.25rem_4.25rem] gap-x-1 px-2 py-2',
                                  isDark ? 'hover:bg-emerald-500/10' : 'hover:bg-emerald-50/40',
                                )}
                              >
                                <div className="pt-0.5">
                                  <span className={cn(
                                    'inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-semibold tabular-nums',
                                    isDark ? 'text-emerald-300 bg-emerald-500/15' : 'text-emerald-800 bg-emerald-100',
                                  )}>
                                    {idx + 1}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <Badge variant="success" className="font-normal text-[9px] uppercase tracking-wide px-1 py-0">Dr</Badge>
                                    <span className={cn(
                                      'text-[10px] font-medium uppercase tracking-wide',
                                      isDark ? 'text-emerald-400' : 'text-emerald-700',
                                    )}>Expense</span>
                                    {share > 0 && (
                                      <span className={cn('text-[10px] tabular-nums', colors.textMuted)}>{share}%</span>
                                    )}
                                  </div>
                                  <p className={cn('text-sm font-medium leading-snug break-words mt-0.5', colors.text)}>{name}</p>
                                  {(row.narrative || description) && (
                                    <p className={cn('text-[11px] mt-0.5 leading-snug break-words', colors.textMuted)}>
                                      {row.narrative || description}
                                    </p>
                                  )}
                                  {usd > 0 && (
                                    <p className={cn('text-[10px] tabular-nums mt-0.5', colors.textMuted)}>USD {usd.toFixed(2)}</p>
                                  )}
                                </div>
                                <div className="text-right pt-0.5">
                                  <p className={cn('text-sm font-semibold tabular-nums', colors.text)}>{round2(amt).toFixed(2)}</p>
                                  {currencyCode && (
                                    <p className={cn('text-[9px] uppercase tracking-wide', colors.textMuted)}>{currencyCode}</p>
                                  )}
                                </div>
                                <div className={cn('text-right pt-0.5 tabular-nums text-sm', colors.textMuted)}>—</div>
                              </div>
                            );
                          })}

                          <div className={cn(
                            'grid grid-cols-[1.75rem_minmax(0,1fr)_4.25rem_4.25rem] gap-x-1 px-2 py-2',
                            isDark ? 'bg-sky-500/10 hover:bg-sky-500/15' : 'bg-sky-50/50 hover:bg-sky-50/80',
                          )}>
                            <div className="pt-0.5">
                              <span className={cn(
                                'inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-semibold tabular-nums',
                                isDark ? 'text-sky-300 bg-sky-500/15' : 'text-sky-800 bg-sky-100',
                              )}>
                                {filledExpenses.length + 1}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="info" className="font-normal text-[9px] uppercase tracking-wide px-1 py-0">Cr</Badge>
                                <span className={cn(
                                  'text-[10px] font-medium uppercase tracking-wide',
                                  isDark ? 'text-sky-400' : 'text-sky-700',
                                )}>Vendor AP</span>
                                {vendorLabel?.allowOpenItems && (
                                  <span className={cn('text-[10px]', colors.textMuted)}>Open item</span>
                                )}
                              </div>
                              <p className={cn('text-sm font-medium leading-snug break-words mt-0.5', colors.text)}>
                                {vendorLabel?.name ?? vendorLabel?.combLevel ?? vendorLabel?.code ?? '—'}
                              </p>
                              <div className={cn('flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[11px]', colors.textMuted)}>
                                {dueDate && <span>Due {formatDate(dueDate) || dueDate}</span>}
                                {dn && <span>DN {dn}</span>}
                              </div>
                              {amountUsdNum > 0 && (
                                <p className={cn('text-[10px] tabular-nums mt-0.5', colors.textMuted)}>USD {amountUsd}</p>
                              )}
                            </div>
                            <div className={cn('text-right pt-0.5 tabular-nums text-sm', colors.textMuted)}>—</div>
                            <div className="text-right pt-0.5">
                              <p className={cn('text-sm font-semibold tabular-nums', colors.text)}>{amountNum.toFixed(2)}</p>
                              {currencyCode && (
                                <p className={cn('text-[9px] uppercase tracking-wide', colors.textMuted)}>{currencyCode}</p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Totals */}
                        <div className={cn('border-t px-2 py-2 space-y-1.5', colors.border, colors.headerBg)}>
                          <div className="grid grid-cols-[1.75rem_minmax(0,1fr)_4.25rem_4.25rem] gap-x-1 items-end">
                            <span />
                            <div className="min-w-0">
                              <p className={cn('text-[10px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                                Totals · {currencyCode || 'Local'}
                              </p>
                              {previewDebitUsd > 0 && (
                                <p className={cn('text-[10px] tabular-nums mt-0.5', colors.textMuted)}>
                                  USD {previewDebitUsd.toFixed(2)} / {previewCreditUsd.toFixed(2)}
                                </p>
                              )}
                            </div>
                            <p className={cn('text-sm font-bold tabular-nums text-right', colors.text)}>
                              {previewDebitLocal.toFixed(2)}
                            </p>
                            <p className={cn('text-sm font-bold tabular-nums text-right', colors.text)}>
                              {previewCreditLocal.toFixed(2)}
                            </p>
                          </div>
                          <div className={cn('flex items-center justify-between gap-2 pt-1 border-t', colors.border)}>
                            <span className={cn('text-[11px]', colors.textMuted)}>
                              {filledExpenses.length} expense{filledExpenses.length === 1 ? '' : 's'} → 1 AP
                            </span>
                            <span className={cn(
                              'text-[11px] font-medium tabular-nums',
                              balanced
                                ? (isDark ? 'text-emerald-400' : 'text-emerald-700')
                                : (isDark ? 'text-amber-400' : 'text-amber-700'),
                            )}>
                              Diff {(previewDebitLocal - previewCreditLocal).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 mt-auto">
          <JournalBalanceBar
            debitLocal={totals.debit}
            creditLocal={totals.credit}
            debitUSD={usdTotals.debit}
            creditUSD={usdTotals.credit}
            diff={totals.diff}
            balanced={balanced}
            companyTotals={companyTotals}
            companyNames={companyNames}
          />
        </div>
      </form>
    </div>
  );
};
